import './App.css'
import { createClient, Provider, cacheExchange } from 'urql'
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
  useHistory,
  Redirect,
} from 'react-router-dom'
import SignIn from './components/sign_in/sign_in'
import Dashboard from './components/dashboard/Dashboard'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { pipe, mergeMap, fromPromise } from 'wonka'
import { print } from 'graphql'

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/graphql'

/**
 * Verifica si el token JWT ha expirado
 * @param {string} token - JWT token
 * @returns {boolean} - true si el token está expirado o es inválido
 */
const isTokenExpired = (token) => {
  if (!token) return true

  try {
    // JWT está en formato: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) return true

    // Decodificar payload (parte 2)
    const payload = JSON.parse(atob(parts[1]))

    // Verificar expiración (exp está en segundos)
    if (!payload.exp) return true

    const now = Math.floor(Date.now() / 1000)
    return payload.exp < now
  } catch (e) {
    console.error('[Security] Error decoding token:', e)
    return true
  }
}

/**
 * Verifica que el email del token sea @smartcfo.cl
 * @param {string} token - JWT token
 * @returns {boolean}
 */
const isValidAdminEmail = (token) => {
  if (!token) return false

  try {
    const parts = token.split('.')
    if (parts.length !== 3) return false

    const payload = JSON.parse(atob(parts[1]))
    const email = payload.email || ''

    return email.endsWith('@smartcfo.cl')
  } catch (e) {
    return false
  }
}

/**
 * Limpia todos los datos de sesión
 */
const clearSession = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('userEmail')
}

// Componente wrapper para pasar location y history
function SignInWrapper({ onLogin }) {
  const location = useLocation()
  const history = useHistory()
  return <SignIn location={location} history={history} onLogin={onLogin} />
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  // Verificar autenticación al cargar y validar token
  useEffect(() => {
    const token = localStorage.getItem('token')
    const email = localStorage.getItem('userEmail')

    if (token) {
      // Verificar que el token no esté expirado
      if (isTokenExpired(token)) {
        console.warn('[Security] Token expired, clearing session')
        clearSession()
        setIsAuthenticated(false)
      } else if (!isValidAdminEmail(token)) {
        console.warn('[Security] Invalid admin email in token')
        clearSession()
        setIsAuthenticated(false)
      } else {
        setIsAuthenticated(true)
        setUserEmail(email || '')
      }
    }

    setIsLoading(false)
  }, [])

  // Exchange personalizado con manejo de errores de autenticación
  const customFetchExchange = useCallback(
    ({ forward }) =>
      (ops$) => {
        return pipe(
          ops$,
          mergeMap((operation) => {
            const token = localStorage.getItem('token')

            // Verificar token antes de cada request
            if (token && isTokenExpired(token)) {
              console.warn('[Security] Token expired during request')
              clearSession()
              setIsAuthenticated(false)
              window.location.href = '/login'
              return fromPromise(
                Promise.resolve({
                  operation,
                  data: undefined,
                  error: { message: 'Sesión expirada' },
                }),
              )
            }

            let query = ''
            if (operation.query.loc?.source?.body) {
              query = operation.query.loc.source.body
            } else if (typeof operation.query === 'string') {
              query = operation.query
            } else if (operation.query.definitions) {
              query = print(operation.query)
            }

            const controller = new AbortController()
            const fetchTimeoutMs = query.includes('PortalHealthSurface')
              ? 12000
              : query.includes('PortalHealth')
                ? 20000
                : 60000
            const timeoutId = setTimeout(() => controller.abort(), fetchTimeoutMs)

            const fetchPromise = fetch(apiUrl, {
              method: 'POST',
              signal: controller.signal,
              headers: {
                'Content-Type': 'application/json',
                'apollo-require-preflight': 'true',
                ...(token ? { authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                query: query,
                variables: operation.variables || {},
                operationName: operation.operationName || null,
              }),
            })
              .then((response) => response.json())
              .then((result) => {
                // Verificar errores de autenticación del servidor
                // EXCEPTO para mutaciones de login (que deben manejar sus propios errores)
                const isLoginMutation = query.includes('mutation') && query.includes('login')
                
                if (result.errors && !isLoginMutation) {
                  // Solo echar al login si la sesi?n JWT muri? o la cuenta est?
                  // desactivada. Un 403 (p. ej. "S?lo el DPO?") o un 401 de
                  // step-up de contrase?a no invalidan la sesi?n.
                  const authError = result.errors.find((e) => {
                    const msg = e.message || ''
                    const code = e.extensions?.code
                    if (msg.includes('desactivada')) return true
                    if (code === 'UNAUTHENTICATED') return true
                    if (msg === 'No autorizado') return true
                    if (msg.includes('Acceso restringido a usuarios')) return true
                    if (msg.includes('Acceso solo para ADMIN')) return true
                    if (
                      (code === 401 || code === 'UNAUTHORIZED') &&
                      (msg.includes('No autorizado') ||
                        msg.includes('Unauthorized') ||
                        msg.includes('token'))
                    ) {
                      return true
                    }
                    return false
                  })

                  if (authError) {
                    console.error('[Security] Auth error from server:', authError.message)
                    clearSession()
                    setIsAuthenticated(false)
                    // Redirigir al login con mensaje
                    window.location.href = '/login?error=' + encodeURIComponent(authError.message)
                  }
                }

                return {
                  operation,
                  data: result.data,
                  error: result.errors
                    ? {
                        graphQLErrors: result.errors,
                        message: result.errors[0]?.message,
                      }
                    : undefined,
                }
              })
              .catch((error) => {
                console.error('Network Error:', error)
                const aborted = error?.name === 'AbortError'
                return {
                  operation,
                  data: undefined,
                  error: {
                    networkError: error,
                    message: aborted
                      ? `Timeout de red (${fetchTimeoutMs / 1000}s)`
                      : error.message,
                  },
                }
              })
              .finally(() => clearTimeout(timeoutId))

            return fromPromise(fetchPromise)
          }),
        )
      },
    [setIsAuthenticated],
  )

  const client = useMemo(() => {
    return createClient({
      url: apiUrl,
      exchanges: [cacheExchange, customFetchExchange],
    })
  }, [customFetchExchange])

  const handleLogin = useCallback((token, email) => {
    // Validar el token antes de guardarlo
    if (!token || isTokenExpired(token)) {
      console.error('[Security] Attempted to save invalid/expired token')
      return
    }

    if (!isValidAdminEmail(token)) {
      console.error('[Security] Token email is not @smartcfo.cl')
      return
    }

    localStorage.setItem('token', token)
    if (email) {
      localStorage.setItem('userEmail', email)
      setUserEmail(email)
    }
    setIsAuthenticated(true)
  }, [])

  const handleLogout = useCallback(() => {
    clearSession()
    setIsAuthenticated(false)
    setUserEmail('')
  }, [])

  // Mostrar loading mientras se verifica la autenticación
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: '#1a1f2e',
        }}
      >
        <div style={{ color: '#94CECC', fontSize: '1.2rem' }}>Verificando sesión...</div>
      </div>
    )
  }

  if (isAuthenticated) {
    return (
      <Provider value={client}>
        <Router>
          <Switch>
            <Route path="/login">
              <Redirect to="/dashboard" />
            </Route>
            <Route path="/">
              <Dashboard userEmail={userEmail} onLogout={handleLogout} />
            </Route>
          </Switch>
        </Router>
      </Provider>
    )
  }

  return (
    <Provider value={client}>
      <Router>
        <Switch>
          <Route path="/reset-password/:token">
            <SignInWrapper onLogin={handleLogin} />
          </Route>
          <Route path="/login">
            <SignInWrapper onLogin={handleLogin} />
          </Route>
          <Route path="/">
            <Redirect to="/login" />
          </Route>
        </Switch>
      </Router>
    </Provider>
  )
}

export default App
